import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clock3, FileUp, FolderOpen, Search, ShieldCheck } from 'lucide-react';
import LaborStandaloneLayout from '../../components/labor/LaborStandaloneLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
  addAdditionalDocuments,
  getLaborCaseByTracking,
} from '../../features/labor-intake/laborIntakeService';

const formatDate = (value) =>
  new Intl.DateTimeFormat('ar-SA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));

const LaborIntakeTrackingPage = () => {
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';
  const [searchParams] = useSearchParams();
  const initialReference = searchParams.get('ref') || '';
  const initialToken = searchParams.get('token') || '';
  const [referenceNumber, setReferenceNumber] = useState(initialReference);
  const [trackingToken, setTrackingToken] = useState(initialToken);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [searchAttempted, setSearchAttempted] = useState(Boolean(initialReference && initialToken));
  const [trackedCase, setTrackedCase] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadCase = async () => {
    if (!referenceNumber || !trackingToken) return;
    setLoading(true);
    try {
      setTrackedCase(await getLaborCaseByTracking({ referenceNumber, trackingToken }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialReference && initialToken) loadCase();
  }, []);

  const handleSearch = async (event) => {
    event.preventDefault();
    setSearchAttempted(true);
    await loadCase();
  };

  const handleUpload = async (event) => {
    if (!trackedCase) return;

    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const updated = await addAdditionalDocuments({
      caseId: trackedCase.id,
      trackingToken: trackedCase.trackingToken,
      files,
    });
    setTrackedCase(updated);
    setUploadedCount(files.length);
    event.target.value = '';
  };

  return (
    <LaborStandaloneLayout footerNote={isArabic ? 'قريباً… يتم إطلاق منصة وازن تدريجياً.' : 'Coming soon... Wazin platform is being launched gradually.'}>
      <main className="px-4 pb-20 pt-12">
          <div className="mx-auto max-w-6xl">
            <div className="grid gap-8 lg:grid-cols-[420px_minmax(0,1fr)]">
              <Card className="h-fit rounded-[28px] border-[#C8A870]/20 bg-white/95 shadow-[0_18px_55px_rgba(15,23,42,0.08)]">
                <CardHeader>
                  <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-[22px] bg-[#1F3B64] text-white">
                    <Search className="h-6 w-6" />
                  </div>
                  <CardTitle className="text-2xl font-black text-[#1F3B64]">{isArabic ? 'متابعة الطلب' : 'Track Request'}</CardTitle>
                  <CardDescription className="text-sm leading-7 text-slate-600">
                    {isArabic ? 'أدخل الرقم المرجعي ورمز المتابعة الخاص لعرض الحالة العامة للطلب ورفع مستندات إضافية عند الحاجة.' : 'Enter the reference number and tracking token to view the request status and upload additional documents when needed.'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSearch} className="space-y-5">
                    <Field
                      label={isArabic ? 'الرقم المرجعي' : 'Reference Number'}
                      value={referenceNumber}
                      onChange={setReferenceNumber}
                      placeholder="WZN-LB-2026-000101"
                    />
                    <Field
                      label={isArabic ? 'رمز المتابعة' : 'Tracking Token'}
                      value={trackingToken}
                      onChange={setTrackingToken}
                      placeholder="مثال: 9A2Fj4L..."
                    />
                    <Button type="submit" className="w-full rounded-xl bg-[#1F3B64] py-6 text-white hover:bg-[#264b7a]">
                      {isArabic ? 'بحث عن الطلب' : 'Search Request'}
                      <Search className="h-4 w-4" />
                    </Button>
                  </form>

                  <div className="mt-6 rounded-[24px] border border-[#C8A870]/15 bg-[#f8f6f1] p-4 text-sm leading-7 text-slate-600">
                    {isArabic ? 'صفحة المتابعة لا تعرض أي تحليلات أو ملاحظات داخلية، وإنما الحالة الحالية فقط والمستندات المرفوعة من جهة العميل.' : 'The tracking page does not display any analysis or internal notes. It only shows the current status and the documents uploaded by the client.'}
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-6">
                {!trackedCase && searchAttempted && !loading ? (
                  <Card className="rounded-[28px] border-[#C8A870]/20 bg-white/95 shadow-[0_18px_55px_rgba(15,23,42,0.08)]">
                    <CardContent className="p-8 text-center">
                      <h2 className="text-2xl font-black text-[#1F3B64]">تعذر العثور على الطلب</h2>
                      <p className="mt-4 text-sm leading-7 text-slate-600">
                        يرجى التأكد من صحة الرقم المرجعي ورمز المتابعة، أو استخدام الرابط المباشر الذي وصل بعد الإرسال.
                      </p>
                      <Button asChild variant="outline" className="mt-6 rounded-xl border-[#C8A870]/30 px-6 py-6 text-[#1F3B64]">
                        <Link to="/labor-intake">العودة إلى بوابة الطلبات</Link>
                      </Button>
                    </CardContent>
                  </Card>
                ) : null}

                {trackedCase ? (
                  <>
                    <Card className="rounded-[28px] border-[#C8A870]/20 bg-white/95 shadow-[0_18px_55px_rgba(15,23,42,0.08)]">
                      <CardHeader>
                        <div className="flex flex-wrap items-center justify-between gap-4">
                          <div>
                            <CardTitle className="text-2xl font-black text-[#1F3B64]">
                              الطلب {trackedCase.referenceNumber}
                            </CardTitle>
                            <CardDescription className="mt-2 text-sm leading-7 text-slate-600">
                              أُرسل بتاريخ {formatDate(trackedCase.submittedAt)}، وتظهر هنا الحالة العامة فقط.
                            </CardDescription>
                          </div>
                          <span className="rounded-full bg-[#A27849]/12 px-4 py-2 text-sm font-bold text-[#A27849]">
                            {trackedCase.statusLabel}
                          </span>
                        </div>
                      </CardHeader>
                      <CardContent className="grid gap-4 md:grid-cols-3">
                        <SummaryBox title="العميل" value={trackedCase.client.fullName || 'غير مدخل'} icon={ShieldCheck} />
                        <SummaryBox title="المنشأة" value={trackedCase.company.employerName || 'غير مدخل'} icon={FolderOpen} />
                        <SummaryBox title="الحالة" value={trackedCase.statusLabel} icon={Clock3} />
                      </CardContent>
                    </Card>

                    <Card className="rounded-[28px] border-[#C8A870]/20 bg-white/95 shadow-[0_18px_55px_rgba(15,23,42,0.08)]">
                      <CardHeader>
                        <CardTitle className="text-xl font-black text-[#1F3B64]">المستندات الإضافية</CardTitle>
                        <CardDescription className="text-sm leading-7 text-slate-600">
                          عند طلب مستندات جديدة منكم، يمكن إضافتها هنا دون إظهار أي بيانات داخلية أخرى.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-5">
                        <div className="rounded-[24px] border border-dashed border-[#C8A870]/35 bg-[#f8f6f1] p-5">
                          <input
                            type="file"
                            multiple
                            onChange={handleUpload}
                            className="block w-full rounded-2xl border border-[#C8A870]/20 bg-white px-4 py-3 text-sm text-slate-700 file:ml-4 file:rounded-xl file:border-0 file:bg-[#1F3B64] file:px-4 file:py-2 file:text-white"
                          />
                          <p className="mt-3 text-sm leading-7 text-slate-600">
                            يفضل تسمية كل ملف بشكل واضح: عقد العمل، مسير رواتب، خطاب فصل، كشف حساب، إلخ.
                          </p>
                          {uploadedCount > 0 ? (
                            <p className="mt-3 text-sm font-semibold text-[#A27849]">
                              تم تسجيل {uploadedCount} مستند/مستندات إضافية لهذا الطلب.
                            </p>
                          ) : null}
                        </div>

                        <div className="grid gap-3">
                          {[...(trackedCase.attachments.additional || []), ...(trackedCase.attachments.initial || [])].map((file) => (
                            <div
                              key={file.id}
                              className="flex items-center justify-between rounded-2xl border border-[#C8A870]/15 bg-white px-4 py-3 text-sm"
                            >
                              <div>
                                <p className="font-bold text-[#1F3B64]">{file.name}</p>
                                <p className="mt-1 text-slate-500">{Math.ceil((file.size || 0) / 1024)} KB</p>
                              </div>
                              <span className="text-xs text-slate-400">{formatDate(file.uploadedAt)}</span>
                            </div>
                          ))}
                          {!(trackedCase.attachments.additional || []).length && !(trackedCase.attachments.initial || []).length ? (
                            <div className="rounded-2xl border border-[#C8A870]/15 bg-white px-4 py-5 text-sm text-slate-500">
                              لا توجد مستندات مرفوعة حتى الآن.
                            </div>
                          ) : null}
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="rounded-[28px] border-[#C8A870]/20 bg-white/95 shadow-[0_18px_55px_rgba(15,23,42,0.08)]">
                      <CardHeader>
                        <CardTitle className="text-xl font-black text-[#1F3B64]">تحديثات الحالة</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {(trackedCase.timeline || []).map((item) => (
                          <div key={item.id} className="rounded-[24px] border border-[#C8A870]/15 bg-[#f8f6f1] p-5">
                            <p className="font-bold text-[#1F3B64]">{item.label}</p>
                            <p className="mt-2 text-sm leading-7 text-slate-600">{item.description}</p>
                            <p className="mt-3 text-xs text-slate-400">{formatDate(item.createdAt)}</p>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  </>
                ) : !searchAttempted ? (
                  <Card className="rounded-[28px] border-[#C8A870]/20 bg-white/95 shadow-[0_18px_55px_rgba(15,23,42,0.08)]">
                    <CardContent className="p-8 text-center">
                      <h2 className="text-2xl font-black text-[#1F3B64]">أدخل بيانات المتابعة</h2>
                      <p className="mt-4 text-sm leading-7 text-slate-600">
                        استخدم الرقم المرجعي ورمز المتابعة الخاصين بطلبكم لعرض الحالة الحالية ورفع أي مستندات إضافية.
                      </p>
                    </CardContent>
                  </Card>
                ) : null}
              </div>
            </div>
          </div>
      </main>
    </LaborStandaloneLayout>
  );
};

const Field = ({ label, value, onChange, ...props }) => (
  <div>
    <label className="mb-2 block text-sm font-bold text-[#1F3B64]">{label}</label>
    <Input
      {...props}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="h-12 rounded-xl border-[#C8A870]/20 bg-[#fcfbf8] focus-visible:ring-[#A27849]"
    />
  </div>
);

const SummaryBox = ({ icon: Icon, title, value }) => (
  <div className="rounded-[24px] border border-[#C8A870]/15 bg-[#f8f6f1] p-5">
    <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-[#1F3B64] text-white">
      <Icon className="h-5 w-5" />
    </div>
    <p className="text-sm font-bold text-[#1F3B64]">{title}</p>
    <p className="mt-2 text-sm leading-7 text-slate-700">{value}</p>
  </div>
);

export default LaborIntakeTrackingPage;
